/**
 * Record a surveyed condition score — build 6B.
 *
 * Condition is measured; health is the platform's combination of it with
 * defects, age and forecast, and is never typed in. This form enters the
 * measured part: one asset, a score 0–100, the survey date and what the score
 * rests on. Health is recomputed at once, so the matrix above moves.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useAmRecordCondition } from "../../api/entryFormQueries";
import { useAmRegister } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { Button, Card, Field, Input, Select, Table } from "../../design/components";
import { BAND_LABEL } from "../../domain/assetManagement";
import { conditionRefusal, refusalText, type AmSurvey } from "../../domain/entryForms";
import { t } from "../../i18n";
import { when } from "./KpiTargetsCard";
import s from "./EntryForms.module.css";

const today = () => new Date().toISOString().slice(0, 10);

export function ConditionSurveyCard({
  recent,
  surveyed,
  assets,
}: {
  recent: AmSurvey[];
  surveyed: number;
  assets: number;
}) {
  const session = useSession();
  const canWrite = hasPermission(session.principal, PERMISSION.assetWrite);
  const [q, setQ] = useState("");
  const register = useAmRegister({ q: q.trim() || undefined });
  const record = useAmRecordCondition();
  const [assetId, setAssetId] = useState("");
  const [score, setScore] = useState("");
  const [on, setOn] = useState(today());
  const [basis, setBasis] = useState("");
  const [why, setWhy] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const options = (register.data?.assets ?? []).slice(0, 50);

  const save = async () => {
    if (!assetId) return setWhy(t("Pick the asset that was surveyed."));
    const input = { score: score.trim() === "" ? Number.NaN : Number(score), surveyedOn: on || undefined, basis };
    const refused = conditionRefusal(input, today());
    if (refused) return setWhy(refused);
    setWhy(null);
    try {
      const r = await record.mutateAsync({ assetId, input });
      setSaved(
        r.healthBand != null
          ? t("{tag}: condition {score} saved. Health is now {health} ({band}).", {
              tag: r.tag,
              score: r.conditionScore,
              health: r.healthScore ?? "—",
              band: t(BAND_LABEL[r.healthBand]),
            })
          : t("{tag}: condition {score} saved.", { tag: r.tag, score: r.conditionScore }),
      );
      setScore("");
      setBasis("");
    } catch (e) {
      setWhy(refusalText(e));
    }
  };

  return (
    <Card
      title="Record a condition survey"
      sub="A measured score from 0 to 100."
      collapsible
      collapseKey="am-condition-survey"
      testId="am-condition-survey"
    >
      <p className={s.muted}>
        {t("{n} of {of} assets have a surveyed score. Health is worked out from it.", {
          n: surveyed.toLocaleString("en"),
          of: assets.toLocaleString("en"),
        })}
      </p>
      {canWrite ? (
        <div className={s.form}>
          <Field label="Find the asset">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Tag or name")} />
          </Field>
          <Field label="Asset">
            <Select value={assetId} onChange={(e) => setAssetId(e.target.value)} data-testid="condition-asset">
              <option value="">{t("Choose…")}</option>
              {options.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.tag} · {a.name}
                  {a.conditionScore != null ? ` (${a.conditionScore})` : ""}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Score (0–100)">
            <Input
              type="number"
              min={0}
              max={100}
              step="any"
              inputMode="decimal"
              value={score}
              onChange={(e) => setScore(e.target.value)}
            />
          </Field>
          <Field label="Survey date">
            <Input type="date" value={on} max={today()} onChange={(e) => setOn(e.target.value)} />
          </Field>
          <div className={s.wide}>
            <Field label="Basis — the survey or method">
              <Input
                value={basis}
                onChange={(e) => setBasis(e.target.value)}
                placeholder={t("e.g. annual pump survey, visual and vibration")}
                maxLength={500}
              />
            </Field>
          </div>
          {why && (
            <p className={`${s.err} ${s.wide}`} role="alert">
              {why}
            </p>
          )}
          {saved && (
            <p className={`${s.ok} ${s.wide}`} role="status">
              {saved}
            </p>
          )}
          <div className={`${s.actions} ${s.wide}`}>
            <Button variant="primary" onClick={() => void save()} disabled={record.isPending}>
              {record.isPending ? t("Saving…") : t("Save the score")}
            </Button>
          </div>
        </div>
      ) : (
        <p className={s.muted}>{t("Only an engineer or an administrator can record a condition score.")}</p>
      )}
      {recent.length > 0 && (
        <div className={s.scroll}>
          <Table>
            <thead>
              <tr>
                <th>{t("Tag")}</th>
                <th className={s.num}>{t("Condition")}</th>
                <th>{t("Surveyed")}</th>
                <th>{t("Basis")}</th>
                <th>{t("Entered by")}</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id} data-testid={`survey-${r.tag}`}>
                  <td>
                    <Link to={`/asset-register/${encodeURIComponent(r.id)}`}>{r.tag}</Link>
                  </td>
                  <td className={s.num}>{r.conditionScore}</td>
                  <td>{when(r.conditionAt)}</td>
                  <td className={s.muted}>{r.conditionBasis ?? ""}</td>
                  <td className={s.muted}>{r.conditionBy ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}
